import { HttpClient, HttpEventType, HttpHeaders, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { hypecastHttpContext } from '@app/core/http/http-context';
import { Logger } from '@app/core/logger.service';
import { ResetPasswordBody, User, UserCreateDto, UserSelfPreferencesDto, UserUpdateDto } from '@app/shared/models/classes/User';
import { environment } from '@env/environment';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { IOAuthService } from '../authentication/oauth/ioauth-service';
import { LoadingState } from '../components/files/models/LoadingState.enum';
import { ApiUrlService } from './api-url.service';
import { EntityService } from './entity.service';
import { Response } from './local/Response';

const log = new Logger('Users Service');

@Injectable()
export class UsersService extends EntityService<User, UserCreateDto, UserUpdateDto> {

  constructor(
    protected httpClient: HttpClient,
    private apiUrlService: ApiUrlService,
    private oAuthService: IOAuthService,
  ) {
    super('users', 'User', httpClient);
  }

  getCurrentUser(): Observable<User> {
    return this.httpClient.get<any>(`${this.apiUrlService.getApiUrl()}api/v1/users/me`).pipe(
      map((result: any) => result.data)
    );
  }

  updateMyPreferences(preferences: UserSelfPreferencesDto): Observable<User> {
    return this.httpClient.patch<Response<User>>('users/me/preferences', preferences).pipe(
      map((result: Response<User>) => result.data),
    );
  }

  getMentionableUsers(searchString: string): Observable<User[]> {
    return this.httpClient.get<any>(`users?filter=${searchString}&isMentionable=true`).pipe(
      map((result: any) => result.data)
    );
  }

  /**
   * Whether the signed-in account has a local password. Accounts created through SSO have
   * none and cannot pass the current-password check of the password change. If the status
   * cannot be read, this answers true so password accounts keep their password change.
   */
  hasLocalPassword(): Observable<boolean> {
    const headers = new HttpHeaders().set('Authorization', `Bearer ${this.oAuthService.getAccessToken()}`);

    return this.httpClient.get<Response<{hasPassword: boolean}>>(
      `${environment.authorizationServerUrl}api/v1/users/me/password`,
      // A failed status check must neither toast nor trigger the 401 re-login.
      {headers, context: hypecastHttpContext({disableApiPrefix: true, skipErrorHandler: true, disableAuthorization: true})},
    ).pipe(
      map((result: Response<{hasPassword: boolean}>) => result.data?.hasPassword !== false),
      catchError((error: unknown) => {
        log.warn('Could not read the password status, keeping the password change available', error);
        return of(true);
      }),
    );
  }

  changePassword(currentPassword: string, newPassword: string): Observable<boolean> {
    let headers = new HttpHeaders();
    const token = this.oAuthService.getAccessToken();
    headers = headers.set('Authorization', `Bearer ${token}`);

    return this.httpClient.patch<Response<boolean>>(
      `${environment.authorizationServerUrl}api/v1/users/me/password`,
      {currentPassword, password: newPassword},
      {headers, context: hypecastHttpContext({disableApiPrefix: true})},
    ).pipe(map((result: Response<boolean>) => result.code.statusCode === 200));
  }

  resetPassword(userId: string, resetPasswordDto: ResetPasswordBody) {
    return this.httpClient.patch<any>(
      `users/${userId}/password`,
      resetPasswordDto
    ).pipe(map((result: Response<boolean>) => result.code.statusCode === 200));
  }

  sendPasswordResetEmail(email: string) {
    return this.httpClient
      .post<any>(`users/password-reset`, { email })
      .pipe(map((result: Response<boolean>) => result.code.statusCode === 200));
  }

  changePermissions(userId: string, permissions: string[], useCaseIds: string[]): Observable<boolean> {
    return this.httpClient
      .put<Response<boolean>>(`users/${userId}/permissions`, { permissions, useCaseIds })
      .pipe(map((result: Response<boolean>) => result.data));
  }

  getPermissions(userId: string): Observable<GetUserPermission> {
    return this.httpClient.get<Response<GetUserPermission>>(`users/${userId}/permissions`).pipe(
      map((result: Response<GetUserPermission>) => result.data)
    );
  }

  completeRegistration(userId: string, token: string, value: any): Observable<string> {
    return this.httpClient.post<Response<string>>(
      `users/${userId}/confirm-email`,
      Object.assign({ password: value, token }),
      {context: hypecastHttpContext({disableAccessToken: true})},
    ).pipe(map((result: Response<string>) => result.data));
  }

  changeProfileImageByUserId(userId: string, image: Blob): Observable<ChangeProfilePictureProgress> {
    const formData = new FormData();
    formData.append('file', image);

    return this.httpClient
      .post(`users/${userId}/images`, formData, { reportProgress: true, observe: 'events' })
      .pipe(
        map((event: any) => {
          if (event.type === HttpEventType.UploadProgress) {
            return new ChangeProfilePictureProgress(Math.round(100 * event.loaded / event.total), LoadingState.Loading);
          } else if (event instanceof HttpResponse) {
            return new ChangeProfilePictureProgress(100, LoadingState.Completed);
          }
          return new ChangeProfilePictureProgress(0, LoadingState.Querying);
        })
      );
  }
}

export class ChangeProfilePictureProgress {
  progress: number = 0;
  state: LoadingState = LoadingState.Initial;

  constructor(_progress: number, _state: LoadingState) {
    this.progress = _progress;
    this.state = _state;
  }
}
export class GetUserPermission {
  permissions: string[] = [];
  useCaseIds: string[] = [];
}
